import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router';
import { ApiError } from '../api/client.js';
import { ALLGEMEINER_FEHLERTEXT } from '../api/errors.js';
import { EmptyState, ErrorState, LoadingState } from '../ui/states.js';
import stile from './vorschau-rahmen.module.css';

type Zustand =
  | { art: 'laedt' }
  | { art: 'da'; html: string }
  | { art: 'leer' }
  | { art: 'fehler'; text: string };

/**
 * Die gerenderte Vorschau eines Ratgebers, eingebettet in die Ergebnisseite wie die Vorschau
 * der Content-Factory (Rueckmeldung 2026-10-13: Ergebnis und Vorschau auf EINER Seite). Geholt
 * ueber die Zwischenschicht, nicht vom Browser direkt bei n8n (Architekturregel 1).
 *
 * Eingebettet statt in einem eigenen Tab, weil ein `<a target="_blank">` keinen
 * `Authorization`-Kopf traegt: Eine Tab-Navigation ist kein `fetch`, und ein eigener Tab
 * haette deshalb einen zweiten Auth-Weg gebraucht (kurzlebiges signiertes Ticket).
 * Entscheidung vom 15.09.2026, siehe Learnings-Log.
 */
export function VorschauRahmen({
  runId,
  holen,
}: {
  runId: string;
  holen: (runId: string) => Promise<string>;
}) {
  const [zustand, setZustand] = useState<Zustand>({ art: 'laedt' });

  useEffect(() => {
    // Ein spaet eintreffendes Ergebnis darf nicht mehr in eine verlassene Seite schreiben --
    // sonst zeigt die Vorschau eines abgebrochenen Aufrufs die des naechsten Laufs.
    let verlassen = false;
    setZustand({ art: 'laedt' });
    holen(runId).then(
      (html) => {
        // Ein erfolgreicher Abruf mit leerem Koerper ist kein Fehler, aber auch kein
        // Ergebnis. Ohne eigenen Zustand stuende hier ein leerer Rahmen, und niemand
        // wuesste, ob die Vorschau leer ist oder die Anzeige kaputt.
        if (!verlassen) setZustand(html.trim() === '' ? { art: 'leer' } : { art: 'da', html });
      },
      (f: unknown) => {
        // Nur der Text der Zwischenschicht wird angezeigt. Ein unerwarteter Fehler traegt
        // den allgemeinen Text: Sein `message` koennte alles enthalten, auch den rohen
        // n8n-Befund, und genau der soll den Nutzer nicht mehr erreichen.
        if (!verlassen) {
          setZustand({
            art: 'fehler',
            text: f instanceof ApiError ? f.text : ALLGEMEINER_FEHLERTEXT,
          });
        }
      },
    );
    return () => {
      verlassen = true;
    };
  }, [holen, runId]);

  return (
    <>
      {zustand.art === 'laedt' && <LoadingState text="Die Vorschau wird geladen …" />}
      {zustand.art === 'fehler' && <ErrorState text={zustand.text} />}
      {zustand.art === 'leer' && (
        <EmptyState
          titel="Keine Vorschau vorhanden"
          text="Der Auftrag hat eine Vorschau hinterlegt, sie kam aber leer zurück."
        />
      )}
      {zustand.art === 'da' && (
        /*
         * Das HTML kommt fertig aufbereitet aus der Zwischenschicht -- samt CSP, ohne
         * Skripte und ohne alles, was zu n8n fuehren wuerde (siehe `vorschau-html.ts` im
         * Backend). Hier wird es NICHT noch einmal angefasst: Ein Browser, der fremdes HTML
         * parst, laedt dabei bereits Ressourcen, und genau deshalb liegt die Aufbereitung
         * dort und nicht hier.
         *
         * `sandbox=""` bleibt trotzdem, als zweite Schicht: kein Skript, kein eigener
         * Origin, keine Formulare -- ohne sie liefe fremdes HTML in unserem Origin, mit
         * Zugriff auf den Firebase-Token.
         */
        <iframe
          className={stile.rahmen}
          title="Vorschau des Ratgebers"
          sandbox=""
          srcDoc={zustand.html}
        />
      )}
    </>
  );
}

/** Alte Adresse `/vorschau` (Links in Mails, Lesezeichen): fuehrt jetzt auf das Ergebnis. */
export function VorschauWeiterleitung() {
  const { runId = '' } = useParams();
  return <Navigate to={`/auftraege/${encodeURIComponent(runId)}/ergebnis`} replace />;
}
